<template>
  <div class="wage-chart-container" ref="chartContainer" @scroll="handleScroll">
    <svg ref="svgElement" class="wage-chart"></svg>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  reactive,
  inject,
} from "vue";
import * as d3 from "d3";
import { useTranslation } from "../../aft/useTranslations";

const props = defineProps({
  benchmarkValue: {
    type: Number,
  },
  additionalBenchmark1: {
    type: Number,
    required: false,
  },
  additionalBenchmark2: {
    type: Number,
    required: false,
  },
  currency: {
    type: String,
    default: "USD",
  },
  interactive: {
    type: Boolean,
    default: true,
  },
});

const { _t } = useTranslation();
const livingWageBenchmarkTranslation = _t(
  "calculation_wizard.horizontal_chart.lwb",
  "Living Wage Benchmark",
);
const tooltipTitle = _t(
  "calculation_wizard.horizontal_chart.tooltip_title",
  "These numbers are displayed in your chart for",
);
const tooltipMonthlyRemuneration = _t(
  "calculation_wizard.horizontal_chart.tooltip_monthly_remuneration",
  "Monthly Remuneration",
);
const tooltipWages = _t(
  "calculation_wizard.horizontal_chart.tooltip_wages",
  "Wages",
);
const tooltipBonuses = _t(
  "calculation_wizard.horizontal_chart.tooltip_bonuses",
  "Bonuses",
);
const tooltipInKindBenefits = _t(
  "calculation_wizard.horizontal_chart.tooltip_in_kind_benefits",
  "In-kind Benefits",
);
const tooltipTotal = _t(
  "calculation_wizard.horizontal_chart.tooltip_total",
  "Total",
);
const tooltipClickNote = _t(
  "calculation_wizard.horizontal_chart.tooltip_click_note",
  "Click on each bar to see how numbers are calculated",
);

const chartContainer = ref(null);
const svgElement = ref(null);
const chartData = ref([]);

let svg, g, xScale;
let width, height;
let barPositions = [];
let benefitsCheckboxHandler = null;
let bonusesCheckboxHandler = null;

// Virtual scrolling state
let barsGroup = null;
let benchmarkGroup = null;
let benchmarkGroupAdditional1 = null;
let benchmarkGroupAdditional2 = null;
let lastRenderedRange = { start: -1, end: -1 };
let scrollRAF = null;
let mouseLeaveTimeout = null;
const VIRTUAL_BUFFER = 20; // Extra rows to render above/below viewport
const VIRTUAL_THRESHOLD = 200; // Enable virtual scrolling when data exceeds this count

// Track selected bar for highlighting when sidebar is open
let selectedBarData = null;
let sidebarCloseHandler = null;
let isSidebarOpen = false;

// Tooltip state
let tooltip = null;

const injectedLoading = inject(
  "livingWageGapCalculationChartDataLoading",
  ref(false),
);
const injectedChartData = inject("livingWageGapCalculationChartData", ref([]));

const visibleComponents = reactive({
  benefits: true,
  bonuses: true,
});

const COLORS = {
  wage: "#75E0A7",
  wageOrange: "#DE703E",
  benefits: "#2CE",
  bonuses: "#6172F3",
  idhBlack: "#2D2D32",
};

// Pattern IDs
const PATTERN_IDS = {
  dotsGreen: "dots-pattern-green",
  dotsOrange: "dots-pattern-orange",
  stripesGreen: "stripes-pattern-green",
  stripesOrange: "stripes-pattern-orange",
};

function createPatternDefinitions(svgElement) {
  // Remove any existing defs
  d3.select(svgElement).select("defs").remove();

  const defs = d3.select(svgElement).append("defs");

  // Dots pattern for bonuses - green background
  const dotsPatternGreen = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.dotsGreen)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 4)
    .attr("height", 4);

  dotsPatternGreen
    .append("rect")
    .attr("width", 4)
    .attr("height", 4)
    .attr("fill", COLORS.wage);

  dotsPatternGreen
    .append("circle")
    .attr("cx", 1)
    .attr("cy", 1)
    .attr("r", 0.8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Dots pattern for bonuses - orange background
  const dotsPatternOrange = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.dotsOrange)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 4)
    .attr("height", 4);

  dotsPatternOrange
    .append("rect")
    .attr("width", 4)
    .attr("height", 4)
    .attr("fill", COLORS.wageOrange);

  dotsPatternOrange
    .append("circle")
    .attr("cx", 1)
    .attr("cy", 1)
    .attr("r", 0.8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Stripes pattern for benefits - green background
  const stripesPatternGreen = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.stripesGreen)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 3)
    .attr("height", 8);

  stripesPatternGreen
    .append("rect")
    .attr("width", 3)
    .attr("height", 8)
    .attr("fill", COLORS.wage);

  stripesPatternGreen
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", 1)
    .attr("height", 8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Stripes pattern for benefits - orange background
  const stripesPatternOrange = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.stripesOrange)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 3)
    .attr("height", 8);

  stripesPatternOrange
    .append("rect")
    .attr("width", 3)
    .attr("height", 8)
    .attr("fill", COLORS.wageOrange);

  stripesPatternOrange
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", 1)
    .attr("height", 8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);
}

function getBonusesPattern(useOrange) {
  return useOrange
    ? `url(#${PATTERN_IDS.dotsOrange})`
    : `url(#${PATTERN_IDS.dotsGreen})`;
}

function getBenefitsPattern(useOrange) {
  return useOrange
    ? `url(#${PATTERN_IDS.stripesOrange})`
    : `url(#${PATTERN_IDS.stripesGreen})`;
}

function formatNumber(value) {
  return Number(value.toFixed(2));
}

function removeTooltip() {
  if (tooltip) {
    tooltip.remove();
    tooltip = null;
  }
}

function showTooltip(event, d) {
  if (!props.interactive) return;
  // Don't show tooltip when sidebar is open
  if (isSidebarOpen) return;

  removeTooltip();

  const wage = +(d.wage || 0);
  const bonuses = +(d.bonuses || 0);
  const benefits = +(d.benefits || 0);
  const total = wage + bonuses + benefits;

  const jobCategory = d.jobCategory || "Unknown";
  const gender = d.gender || "";

  // Check if mobile (viewport width < 768px)
  const isMobile = window.innerWidth < 768;

  // Create tooltip container
  tooltip = d3
    .select("body")
    .append("div")
    .attr("class", "wage-chart-tooltip")
    .style("position", "absolute")
    .style("background", "#3B3B44")
    .style("color", "#fff")
    .style("padding", "16px")
    .style("border-radius", "12px")
    .style("font-size", "12px")
    .style("pointer-events", "none")
    .style("z-index", "1000")
    .style("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.15)")
    .style("max-width", isMobile ? "calc(100vw - 20px)" : "300px")
    .style("min-width", isMobile ? "200px" : "220px");

  // Title
  tooltip
    .append("div")
    .style("font-weight", "700")
    .style("font-size", "14px")
    .style("margin-bottom", "16px")
    .style("color", "#fff")
    .html(
      `${
        tooltipTitle.value
      } ${gender.toLowerCase()} ${jobCategory.toLowerCase()}`,
    );

  // Content container
  const contentContainer = tooltip.append("div");

  // Monthly Remuneration section
  const leftColumn = contentContainer.append("div");

  leftColumn
    .append("div")
    .style("font-weight", "600")
    .style("font-size", "12px")
    .style("margin-bottom", "12px")
    .style("color", "#fff")
    .html(tooltipMonthlyRemuneration.value);

  // Wage row
  leftColumn
    .append("div")
    .style("display", "flex")
    .style("justify-content", "space-between")
    .style("margin-bottom", "6px")
    .style("color", "rgba(255,255,255,0.8)")
    .html(
      `<span>${tooltipWages.value}</span><span>${formatNumber(wage)}</span>`,
    );

  // Bonuses row
  leftColumn
    .append("div")
    .style("display", "flex")
    .style("justify-content", "space-between")
    .style("margin-bottom", "6px")
    .style("color", "rgba(255,255,255,0.8)")
    .html(
      `<span>${tooltipBonuses.value}</span><span>${
        bonuses > 0 ? formatNumber(bonuses) : "-"
      }</span>`,
    );

  // Benefits row
  leftColumn
    .append("div")
    .style("display", "flex")
    .style("justify-content", "space-between")
    .style("margin-bottom", "10px")
    .style("color", "rgba(255,255,255,0.8)")
    .html(
      `<span>${tooltipInKindBenefits.value}</span><span>${
        benefits > 0 ? formatNumber(benefits) : "-"
      }</span>`,
    );

  // Total row
  leftColumn
    .append("div")
    .style("display", "flex")
    .style("justify-content", "space-between")
    .style("font-weight", "700")
    .style("color", "#fff")
    .style("border-top", "1px solid rgba(255,255,255,0.2)")
    .style("padding-top", "8px")
    .html(
      `<span>${tooltipTotal.value}</span><span>${formatNumber(total)}</span>`,
    );

  // Footer note with icon
  const footer = tooltip
    .append("div")
    .style("display", "flex")
    .style("align-items", "center")
    .style("gap", "8px")
    .style("margin-top", "16px")
    .style("padding-top", "12px")
    .style("border-top", "1px solid rgba(255,255,255,0.1)")
    .style("color", "rgba(255,255,255,0.7)")
    .style("font-size", "11px");

  footer.html(`
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1Z" stroke="currentColor" stroke-width="1.5"/>
      <path d="M8 11V7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
    </svg>
    <span>${tooltipClickNote.value}</span>
  `);

  // Position tooltip first (before arrow to calculate arrow position)
  const node = event.currentTarget;
  const rect = node.getBoundingClientRect();
  const tooltipNode = tooltip.node();
  const tooltipRect = tooltipNode.getBoundingClientRect();

  // Position at the beginning of the bar (left side)
  let left = rect.left + window.scrollX;

  // Ensure tooltip doesn't go off screen
  const viewportWidth = window.innerWidth;
  if (left < 10) left = 10;
  if (left + tooltipRect.width > viewportWidth - 10) {
    left = viewportWidth - tooltipRect.width - 10;
  }

  // Position above the bar
  const top = rect.top + window.scrollY - tooltipRect.height - 12;

  tooltip.style("left", `${left}px`).style("top", `${top}px`);

  // Arrow pointing down - positioned at beginning of bar
  const arrowLeft = rect.left + window.scrollX - left + 20; // 20px from the left edge of tooltip
  tooltip
    .append("div")
    .style("position", "absolute")
    .style("left", `${arrowLeft}px`)
    .style("bottom", "-8px")
    .style("width", "0")
    .style("height", "0")
    .style("border-left", "8px solid transparent")
    .style("border-right", "8px solid transparent")
    .style("border-top", "8px solid #3B3B44");
}

const CHART_CONFIG = {
  minHeight: 20,
  maxHeight: 100,
  spacing: 3,
  borderRadius: 4,
  defaultMaxValue: 1000,
  benchmarkLabelHeight: 34,
  benchmarkLabelOffsetY: 26,
  segmentLabelMinWidth: 40,
  segmentLabelOffsetX: 8,
};

const ROW_SELECTORS = ".row-group";

function getWageColor(useOrange) {
  return useOrange ? COLORS.wageOrange : COLORS.wage;
}

function shouldUseOrangeWage(total, benchmark) {
  return total < benchmark;
}

function createSegmentPath(
  xOffset,
  segmentWidth,
  barHeight,
  segmentIndex,
  totalSegments,
) {
  const borderRadius = CHART_CONFIG.borderRadius;

  if (segmentIndex === 0) {
    if (segmentIndex === totalSegments - 1) {
      return `
        M ${xOffset + borderRadius} 0
        L ${xOffset + segmentWidth - borderRadius} 0
        Q ${xOffset + segmentWidth} 0 ${xOffset + segmentWidth} ${borderRadius}
        L ${xOffset + segmentWidth} ${barHeight - borderRadius}
        Q ${xOffset + segmentWidth} ${barHeight} ${
          xOffset + segmentWidth - borderRadius
        } ${barHeight}
        L ${xOffset + borderRadius} ${barHeight}
        Q ${xOffset} ${barHeight} ${xOffset} ${barHeight - borderRadius}
        L ${xOffset} ${borderRadius}
        Q ${xOffset} 0 ${xOffset + borderRadius} 0
        Z
      `;
    }
    return `
      M ${xOffset + borderRadius} 0
      L ${xOffset + segmentWidth} 0
      L ${xOffset + segmentWidth} ${barHeight}
      L ${xOffset + borderRadius} ${barHeight}
      Q ${xOffset} ${barHeight} ${xOffset} ${barHeight - borderRadius}
      L ${xOffset} ${borderRadius}
      Q ${xOffset} 0 ${xOffset + borderRadius} 0
      Z
    `;
  } else if (segmentIndex === totalSegments - 1) {
    return `
      M ${xOffset} 0
      L ${xOffset + segmentWidth - borderRadius} 0
      Q ${xOffset + segmentWidth} 0 ${xOffset + segmentWidth} ${borderRadius}
      L ${xOffset + segmentWidth} ${barHeight - borderRadius}
      Q ${xOffset + segmentWidth} ${barHeight} ${
        xOffset + segmentWidth - borderRadius
      } ${barHeight}
      L ${xOffset} ${barHeight}
      Z
    `;
  } else {
    return `
      M ${xOffset} 0
      L ${xOffset + segmentWidth} 0
      L ${xOffset + segmentWidth} ${barHeight}
      L ${xOffset} ${barHeight}
      Z
    `;
  }
}

function handleResize() {
  if (svg && chartContainer.value) {
    renderChart();
  }
}

function syncChartData(sourceData) {
  chartData.value = Array.isArray(sourceData) ? [...sourceData] : [];
}

watch(
  () => [injectedChartData.value, injectedLoading.value],
  ([providedChartData]) => {
    syncChartData(providedChartData);
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  nextTick(() => {
    initializeChart();
    renderChart();
  });

  window.addEventListener("resize", handleResize);

  // Listen for sidebar close event to reset bar highlighting
  sidebarCloseHandler = handleSidebarClose;
  document.addEventListener("close-calculation-sidebar", sidebarCloseHandler);

  const benefitsCheckbox = document.getElementById(
    "chart-filter-benefits-horizontal",
  );
  const bonusesCheckbox = document.getElementById(
    "chart-filter-bonuses-horizontal",
  );

  if (benefitsCheckbox) {
    benefitsCheckbox.checked = visibleComponents.benefits;
    benefitsCheckboxHandler = (e) => {
      visibleComponents.benefits = e.target.checked;
    };
    benefitsCheckbox.addEventListener("change", benefitsCheckboxHandler);
  }

  if (bonusesCheckbox) {
    bonusesCheckbox.checked = visibleComponents.bonuses;
    bonusesCheckboxHandler = (e) => {
      visibleComponents.bonuses = e.target.checked;
    };
    bonusesCheckbox.addEventListener("change", bonusesCheckboxHandler);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);

  if (scrollRAF) {
    cancelAnimationFrame(scrollRAF);
  }

  // Remove tooltip if it exists
  removeTooltip();

  // Remove sidebar close event listener
  if (sidebarCloseHandler) {
    document.removeEventListener(
      "close-calculation-sidebar",
      sidebarCloseHandler,
    );
  }

  const benefitsCheckbox = document.getElementById(
    "chart-filter-benefits-horizontal",
  );
  const bonusesCheckbox = document.getElementById(
    "chart-filter-bonuses-horizontal",
  );

  if (benefitsCheckbox && benefitsCheckboxHandler) {
    benefitsCheckbox.removeEventListener("change", benefitsCheckboxHandler);
  }

  if (bonusesCheckbox && bonusesCheckboxHandler) {
    bonusesCheckbox.removeEventListener("change", bonusesCheckboxHandler);
  }
});

// Handle scroll for virtual rendering
function handleScroll() {
  if (!useVirtualScrolling()) return;

  // Use requestAnimationFrame to throttle scroll updates
  if (scrollRAF) {
    cancelAnimationFrame(scrollRAF);
  }
  scrollRAF = requestAnimationFrame(() => {
    renderVisibleBars();
  });
}

// Check if we should use virtual scrolling
function useVirtualScrolling() {
  return chartData.value.length > VIRTUAL_THRESHOLD;
}

watch(
  () => chartData.value,
  () => {
    if (svg) {
      renderChart();
    }
  },
  { deep: true },
);

watch(
  () => visibleComponents,
  () => {
    if (svg) {
      renderChart();
    }
  },
  { deep: true },
);

function initializeChart() {
  if (!chartContainer.value || !svgElement.value) return;

  d3.select(svgElement.value).selectAll("*").remove();

  width = chartContainer.value.clientWidth;

  height = 0;

  svg = d3.select(svgElement.value).attr("width", width).attr("height", height);

  // Create pattern definitions for bonuses (dots) and benefits (stripes)
  createPatternDefinitions(svgElement.value);

  g = svg.append("g");

  const maxDataValue = d3.max(chartData.value, (d) => d.totalRemuneration) || 0;
  const maxValue = Math.max(props.benchmarkValue || 0, maxDataValue, 1);
  xScale = d3
    .scaleLinear()
    .domain([0, maxValue * 1.2])
    .range([0, width]);
}

function renderChart() {
  if (!g || !xScale) return;

  g.selectAll("*").remove();
  lastRenderedRange = { start: -1, end: -1 };

  const sortedData = [...chartData.value];

  width = chartContainer.value.clientWidth;

  const maxDataValue = d3.max(chartData.value, (d) => d.totalRemuneration) || 0;
  const maxValue = Math.max(props.benchmarkValue || 0, maxDataValue, 1);
  xScale = d3
    .scaleLinear()
    .domain([0, maxValue * 1.2])
    .range([0, width]);

  let cumulativeY = 0;

  const allWorkers = ref(0);
  sortedData.forEach((d) => {
    allWorkers.value += d.workerCount;
  });

  // Calculate all bar positions (just math, very fast)
  barPositions = sortedData.map((d, index) => {
    const workerCount = d.workerCount || 1;
    const workerPercent = workerCount / allWorkers.value;
    const barHeight =
      workerPercent * CHART_CONFIG.maxHeight + CHART_CONFIG.minHeight;
    const useOrange = shouldUseOrangeWage(
      d.totalRemuneration,
      props.benchmarkValue,
    );

    const position = {
      data: d,
      y: cumulativeY,
      height: barHeight,
      useOrange,
      index,
    };
    cumulativeY += barHeight + CHART_CONFIG.spacing;
    return position;
  });

  height = cumulativeY;

  // if we have no additional benchmarks, total chart height = bar heights + benchmark label height + 4 px gap
  if (!props.additionalBenchmark1 && !props.additionalBenchmark2) {
    svg.attr("height", height + 4 + CHART_CONFIG.benchmarkLabelHeight);
  }
  // if we have only one additional benchmark, total chart height = bar heights + 4 px gap + 2 benchmark label height + 5 gap between labels
  if (props.additionalBenchmark1 || props.additionalBenchmark2) {
    svg.attr("height", height + 4 + CHART_CONFIG.benchmarkLabelHeight * 2 + 5);
  }
  // if we have only one additional benchmark, total chart height = bar heights + 4 px gap + 3 benchmark label height + 5 gap between labels each time
  if (props.additionalBenchmark1 && props.additionalBenchmark2) {
    svg.attr("height", height + 4 + CHART_CONFIG.benchmarkLabelHeight * 3 + 10);
  }

  // Create a group for bars (will be updated on scroll for virtual rendering)
  barsGroup = g.append("g").attr("class", "bars-group");

  // Render benchmark (always visible)
  renderBenchmark();

  // Use virtual scrolling for large datasets
  if (useVirtualScrolling()) {
    renderVisibleBars();
  } else {
    // For small datasets, render all bars at once
    renderBarsInRange(0, barPositions.length - 1);
  }
}

// Render only bars visible in the viewport
function renderVisibleBars() {
  if (!chartContainer.value || !barsGroup) return;

  const containerRect = chartContainer.value.getBoundingClientRect();
  const scrollTop = chartContainer.value.scrollTop;
  const viewportTop = scrollTop;
  const viewportBottom = scrollTop + containerRect.height;

  // Find visible range using binary search for efficiency
  let startIndex = findFirstVisibleIndex(viewportTop);
  let endIndex = findLastVisibleIndex(viewportBottom);

  // Add buffer for smoother scrolling
  startIndex = Math.max(0, startIndex - VIRTUAL_BUFFER);
  endIndex = Math.min(barPositions.length - 1, endIndex + VIRTUAL_BUFFER);

  // Only re-render if the range has changed significantly
  if (
    startIndex === lastRenderedRange.start &&
    endIndex === lastRenderedRange.end
  ) {
    return;
  }

  renderBarsInRange(startIndex, endIndex);
  lastRenderedRange = { start: startIndex, end: endIndex };
}

// Binary search to find first visible bar index
function findFirstVisibleIndex(viewportTop) {
  let low = 0;
  let high = barPositions.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    const bar = barPositions[mid];
    if (bar.y + bar.height < viewportTop) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

// Binary search to find last visible bar index
function findLastVisibleIndex(viewportBottom) {
  let low = 0;
  let high = barPositions.length - 1;

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    const bar = barPositions[mid];
    if (bar.y > viewportBottom) {
      high = mid - 1;
    } else {
      low = mid;
    }
  }
  return high;
}

// Render bars in a specific range
function renderBarsInRange(startIndex, endIndex) {
  if (!barsGroup) return;

  // Clear existing bars
  barsGroup.selectAll("*").remove();

  const visiblePositions = barPositions.slice(startIndex, endIndex + 1);

  const rows = barsGroup
    .selectAll(".row-group")
    .data(visiblePositions, (d) => d.index)
    .enter()
    .append("g")
    .attr("class", "row-group")
    .attr("transform", (d) => `translate(0,${d.y})`)
    .style("transition", "opacity 200ms ease-out");

  if (props.interactive) {
    attachRowEvents(rows);
  }

  rows.each(function (d) {
    renderSingleBar(d3.select(this), d);
  });

  // Re-apply selected bar highlight if sidebar is open
  if (isSidebarOpen && selectedBarData) {
    setRowOpacity(selectedBarData, 1);
    fadeOtherRows(selectedBarData);
  }
}

// Render a single bar
function renderSingleBar(rowGroup, d) {
  const barHeight = d.height;
  const data = d.data;

  const barGroup = rowGroup.append("g").attr("class", "bar-group");
  let xOffset = 0;

  const segments = ["wage"];

  if (visibleComponents.benefits && +data.benefits > 0) {
    segments.push("benefits");
  }

  if (visibleComponents.bonuses && +data.bonuses > 0) {
    segments.push("bonuses");
  }

  segments.forEach((segment, segmentIndex) => {
    const segmentValue = data[segment] || 0;
    const segmentWidth = xScale(segmentValue);

    // Determine fill: wage uses solid color, bonuses uses dots pattern, benefits uses stripes pattern
    let fillColor;
    if (segment === "wage") {
      fillColor = getWageColor(d.useOrange);
    } else if (segment === "bonuses") {
      fillColor = getBonusesPattern(d.useOrange);
    } else if (segment === "benefits") {
      fillColor = getBenefitsPattern(d.useOrange);
    } else {
      fillColor = COLORS[segment];
    }

    barGroup
      .append("path")
      .attr("class", `segment segment-${segment}`)
      .attr(
        "d",
        createSegmentPath(
          xOffset,
          segmentWidth,
          barHeight,
          segmentIndex,
          segments.length,
        ),
      )
      .attr("fill", fillColor);

    // if it is the wage segment, we also add the info label directly
    if (segment === "wage") {
      const labelText = `${data.workerId ? data.workerId + " - " : ""}  ${
        data.jobCategory || ""
      } - ${data.workArea || ""} - ${data.gender || ""}`;

      rowGroup
        .append("text")
        .attr("class", "segment-label segment-label-wage")
        .attr("x", xOffset + 8)
        .attr("y", barHeight / 2)
        .attr("alignment-baseline", "middle")
        .attr("text-anchor", "start")
        .attr("fill", "#0D2D3A")
        .attr("font-size", "12px")
        .attr("font-weight", "400")
        .style("pointer-events", "none")
        .text(labelText);
    }

    xOffset += segmentWidth;
  });

  const totalLabel = rowGroup
    .append("g")
    .attr("class", "total-label")
    .attr("transform", `translate(${width},${barHeight / 2})`);

  totalLabel
    .append("text")
    .attr("class", "total-value")
    .attr("text-anchor", "end")
    .attr("alignment-baseline", "middle")
    .attr("fill", "black")
    .attr("font-size", "12px")
    .attr("font-weight", "400")
    .text(`${data.totalRemuneration || 0} ${props.currency}`);
}

// Render benchmark line (separate from bars so it's always visible)
function renderBenchmark() {
  if (!props.benchmarkValue || props.benchmarkValue <= 0) return;

  const x = xScale(props.benchmarkValue);

  benchmarkGroup = g.append("g").attr("class", "benchmark-group");

  benchmarkGroup
    .append("circle")
    .attr("cx", x)
    .attr("cy", 4)
    .attr("r", 4)
    .attr("fill", COLORS.idhBlack);

  benchmarkGroup
    .append("line")
    .attr("x1", x)
    .attr("y1", 0)
    .attr("x2", x)
    .attr("y2", height + 4)
    .attr("stroke", COLORS.idhBlack)
    .attr("stroke-width", 2);

  const labelWidth = 300;
  // if x is smaller than half the label size, we start the label at the beginning of the chart (no left overflow), otherwise we start the label at x - half label width (so x line is in the middle of the label)
  const minX = x < labelWidth / 2 ? 0 : x - labelWidth / 2;
  // in cases where x is really big, the label should not overflow on the right
  const realX = Math.min(minX, width - labelWidth);

  //add foreign object to show benchmark value label
  const fo = benchmarkGroup
    .append("foreignObject")
    .attr("x", realX)
    .attr("y", height + 4)
    .attr("width", labelWidth)
    .attr("height", CHART_CONFIG.benchmarkLabelHeight);

  fo
    .append("xhtml:div")
    .attr(
      "class",
      `w-full bg-[#2D2D32] h-[34px] rounded-lg flex items-center justify-center gap-x-2 py-2 px-3 text-xs text-white font-semibold`,
    ).html(`
    <div class="h-3 w-3 bg-success-300 rounded-full"></div>
    <span class="hidden sm:block">${livingWageBenchmarkTranslation.value}: ${props.benchmarkValue} ${props.currency}</span>
    <span class="sm:hidden">LWB: ${props.benchmarkValue} ${props.currency}</span>
  `);

  const labelWidthAdditional = 120;
  const benchmark1HeightPosition =
    height + 4 + CHART_CONFIG.benchmarkLabelHeight + 5;
  const benchmark2HeightPosition = props.additionalBenchmark1
    ? height + 4 + CHART_CONFIG.benchmarkLabelHeight * 2 + 5 * 2
    : height + 4 + CHART_CONFIG.benchmarkLabelHeight + 5;

  if (props.additionalBenchmark1) {
    const xAdditional1 = xScale(props.additionalBenchmark1);

    benchmarkGroupAdditional1 = g.append("g").attr("class", "benchmark-group");

    benchmarkGroupAdditional1
      .append("circle")
      .attr("cx", xAdditional1)
      .attr("cy", 4)
      .attr("r", 4)
      .attr("fill", COLORS.idhBlack);

    benchmarkGroupAdditional1
      .append("line")
      .attr("x1", xAdditional1)
      .attr("y1", 0)
      .attr("x2", xAdditional1)
      .attr("y2", benchmark1HeightPosition)
      .attr("stroke", COLORS.idhBlack)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "4 4");

    const minXAdditional1 =
      xAdditional1 < labelWidthAdditional / 2
        ? 0
        : xAdditional1 - labelWidthAdditional / 2;
    const realXAdditional1 = Math.min(
      minXAdditional1,
      width - labelWidthAdditional,
    );

    //add foreign object to show benchmark value label
    const foAdditional1 = benchmarkGroupAdditional1
      .append("foreignObject")
      .attr("x", realXAdditional1)
      .attr("y", benchmark1HeightPosition)
      .attr("width", labelWidthAdditional)
      .attr("height", CHART_CONFIG.benchmarkLabelHeight);

    foAdditional1
      .append("xhtml:div")
      .attr(
        "class",
        `w-full bg-[#2D2D32] h-[34px] rounded-lg flex items-center justify-center gap-x-2 py-2 px-3 text-xs text-white font-semibold`,
      ).html(`
    <div class="h-3 w-3 bg-success-300 rounded-full"></div>
    <span class="hidden sm:block">${props.additionalBenchmark1} ${props.currency}</span>
  `);
  }

  if (props.additionalBenchmark2) {
    const xAdditional2 = xScale(props.additionalBenchmark2);

    benchmarkGroupAdditional2 = g.append("g").attr("class", "benchmark-group");

    benchmarkGroupAdditional2
      .append("circle")
      .attr("cx", xAdditional2)
      .attr("cy", 4)
      .attr("r", 4)
      .attr("fill", COLORS.idhBlack);

    benchmarkGroupAdditional2
      .append("line")
      .attr("x1", xAdditional2)
      .attr("y1", 0)
      .attr("x2", xAdditional2)
      .attr("y2", benchmark2HeightPosition)
      .attr("stroke", COLORS.idhBlack)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "2 2");

    const minXAdditional2 =
      xAdditional2 < labelWidthAdditional / 2
        ? 0
        : xAdditional2 - labelWidthAdditional / 2;
    const realXAdditional2 = Math.min(
      minXAdditional2,
      width - labelWidthAdditional,
    );

    //add foreign object to show benchmark value label
    const foAdditional2 = benchmarkGroupAdditional2
      .append("foreignObject")
      .attr("x", realXAdditional2)
      .attr("y", benchmark2HeightPosition)
      .attr("width", labelWidthAdditional)
      .attr("height", CHART_CONFIG.benchmarkLabelHeight);

    foAdditional2
      .append("xhtml:div")
      .attr(
        "class",
        `w-full bg-[#2D2D32] h-[34px] rounded-lg flex items-center justify-center gap-x-2 py-2 px-3 text-xs text-white font-semibold`,
      ).html(`
    <div class="h-3 w-3 bg-success-300 rounded-full"></div>
    <span class="hidden sm:block">${props.additionalBenchmark2} ${props.currency}</span>
  `);
  }
}

function attachRowEvents(selection) {
  if (!props.interactive) return;

  selection
    .style("cursor", "pointer")
    .on("mouseenter", function (event, d) {
      handleMouseEnter(event, d.data);
    })
    .on("mouseleave", function () {
      handleMouseLeave();
    })
    .on("click", function (event, d) {
      handleBarClick(event, d.data);
    });
}

function setRowOpacity(data, opacity) {
  if (!props.interactive) return;
  d3.selectAll(ROW_SELECTORS)
    .filter((other) => other.data === data)
    .style("opacity", opacity)
    .classed("selected-bar", opacity === 1 && isSidebarOpen);
}

function fadeOtherRows(data) {
  if (!props.interactive) return;
  d3.selectAll(ROW_SELECTORS)
    .filter((other) => other.data !== data)
    .style("opacity", 0.3)
    .classed("selected-bar", false);
}

function resetAllRowsOpacity() {
  if (!props.interactive) return;
  d3.selectAll(ROW_SELECTORS)
    .style("opacity", 1)
    .classed("selected-bar", false);
}

function handleMouseEnter(event, d) {
  if (!props.interactive) return;
  // Don't override highlighting when sidebar is open
  if (isSidebarOpen) return;
  // Cancel any pending mouseleave reset
  if (mouseLeaveTimeout) {
    clearTimeout(mouseLeaveTimeout);
    mouseLeaveTimeout = null;
  }
  setRowOpacity(d, 1);
  fadeOtherRows(d);
  // Show tooltip
  showTooltip(event, d);
}

function handleMouseLeave() {
  if (!props.interactive) return;
  // Remove tooltip
  removeTooltip();
  // Don't reset opacity if sidebar is open or a bar is selected
  if (isSidebarOpen || selectedBarData) return;
  // Delay the reset to prevent flickering when moving between bars
  if (mouseLeaveTimeout) {
    clearTimeout(mouseLeaveTimeout);
  }
  mouseLeaveTimeout = setTimeout(() => {
    // Double-check sidebar hasn't opened during the timeout
    if (!isSidebarOpen && !selectedBarData) {
      resetAllRowsOpacity();
    }
    mouseLeaveTimeout = null;
  }, 50);
}

function handleBarClick(event, d) {
  if (!props.interactive) return;
  // Remove tooltip when clicking to open sidebar
  removeTooltip();
  // Set the selected bar for highlighting
  selectedBarData = d;
  isSidebarOpen = true;
  // Ensure the selected bar stays highlighted
  setRowOpacity(d, 1);
  fadeOtherRows(d);
  document.dispatchEvent(new CustomEvent("open-calculation-sidebar"));
  document.dispatchEvent(
    new CustomEvent("chart-bar-selected", {
      detail: {
        bar: d,
        currency: props.currency,
      },
    }),
  );
}

function handleSidebarClose() {
  // Clear the selected bar when sidebar closes
  selectedBarData = null;
  isSidebarOpen = false;
  resetAllRowsOpacity();
}
</script>

<style scoped>
.wage-chart-container {
  width: 100%;
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}

.wage-chart {
  display: block;
  width: 100%;
}

:deep(.row-group) {
  transition: opacity 200ms ease-out;
}

:deep(.row-group:not(.interactive)) {
  transition: none;
}

/* Ensure selected bar stays visible when sidebar is open */
:deep(.row-group.selected-bar) {
  opacity: 1 !important;
}
</style>
